// src/pages/ViewInvoice.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { STORAGE_KEYS } from '../../utils/constants';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  TruckIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = import.meta.env.VITE_BASE_URL;

// ─── Formatters (EXACT SAME AS ADMIN) ───────────────────────────────────────
const fmt = (v) => v || '—';
const fmtAmt = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

function makeInvoiceNumber(invoice) {
  const year = new Date(invoice.created_at || Date.now()).getFullYear();
  return `INV-${year}-${String(invoice.booking_id).padStart(6, '0')}`;
}

function getServiceRows(invoice) {
  const rows = invoice.services || invoice.bookingServices || [];
  return rows.map((row, index) => ({
    srNo: index + 1,
    name:
      row?.service?.service_name ||
      row?.service_name ||
      row?.custom_service_name ||
      'Service Item',
    qty: Number(row?.quantity || 1),
    rate: Number(row?.price || row?.rate || row?.amount || 0),
    total:
      Number(row?.total_amount || 0) ||
      Number(row?.quantity || 1) * Number(row?.price || row?.rate || row?.amount || 0),
  }));
}

// ─── Status Badge (EXACT SAME) ──────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    Pending: 'bg-amber-100 text-amber-700 border-amber-300',
    Accepted: 'bg-blue-100 text-blue-700 border-blue-300',
    'Vehicle Pick': 'bg-cyan-100 text-cyan-700 border-cyan-300',
    'Servicing in Process': 'bg-violet-100 text-violet-700 border-violet-300',
    'Servicing Done': 'bg-teal-100 text-teal-700 border-teal-300',
    'Vehicle Drop': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    Complete: 'bg-green-100 text-green-700 border-green-300',
    'Order Completed': 'bg-green-100 text-green-700 border-green-300',
    Completed: 'bg-green-100 text-green-700 border-green-300',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${cls}`}>
      {status}
    </span>
  );
}

// ─── Info Row (EXACT SAME) ──────────────────────────────────────────────────
function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500 shrink-0" style={{ minWidth: 110 }}>{label}</span>
      <span className={`text-xs font-semibold text-right text-gray-800 break-all ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}

// ─── Section Card (EXACT SAME) ──────────────────────────────────────────────
function SCard({ title, icon, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <span className="text-base">{icon}</span>
        <h3 className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">{title}</h3>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

// ─── Main Customer Component ────────────────────────────────────────────────
const ViewInvoice = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const printRef = useRef();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');

  // ─── Fetch Invoice (Customer API) ─────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !bookingId) {
      navigate('/servicing/history');
      return;
    }

    let active = true;
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);

        const { data } = await axios.get(`${API_BASE_URL}/booking/${bookingId}/invoice`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (active && data?.success && data.data) {
          setInvoice(data.data);
        } else if (active) {
          setError(data?.message || 'Invoice not found');
        }
      } catch (err) {
        if (active) {
          setError(
            err.response?.status === 401
              ? 'Session expired. Please login again.'
              : err.response?.data?.message || 'Failed to load invoice'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchInvoice();
    return () => {
      active = false;
    };
  }, [bookingId, isAuthenticated, navigate]);

  const serviceRows = useMemo(() => (invoice ? getServiceRows(invoice) : []), [invoice]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6" />
          <p className="text-xl font-semibold text-gray-700">Loading your invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center border-2 border-red-200">
          <div className="w-24 h-24 bg-red-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <DocumentTextIcon className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invoice Not Found</h2>
          <p className="text-red-700 mb-8 text-lg">{error}</p>
          <button
            onClick={() => navigate('/servicing/history')}
            className="px-8 py-4 bg-red-600 text-white rounded-2xl font-bold text-lg hover:bg-red-700 transition-all flex items-center gap-3 mx-auto shadow-xl"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Service History
          </button>
        </div>
      </div>
    );
  }

  const invoiceNo = makeInvoiceNumber(invoice);
  const issueDate = invoice.updated_at || invoice.created_at;
  const subtotal = Number(invoice.subtotal || 0);
  const gst = Number(invoice.gst_amount || invoice.gstamount || 0);
  const total = Number(invoice.total_amount || invoice.totalamount || 0);
  const isDoorstep = invoice.pickup_drop === 1 || invoice.pickupdrop === 1;
  const doorstepCharge = isDoorstep ? 100 : 0;
  const paymentStatus = invoice.payment_status || invoice.paymentstatus || 'Pending';
  const bookingStatus = invoice.booking_status || invoice.bookingstatus || '—';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-6 sm:py-8">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-break { page-break-inside: avoid; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-3 sm:px-5 lg:px-8 py-4 sm:py-6 pb-16">
        {/* ── Top Action Bar ──────────────────────────────────────────── */}
        <div className="no-print flex items-center justify-between gap-3 mb-5 flex-wrap">
          <button
            onClick={() => navigate('/servicing/history')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-semibold transition flex-shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to History
          </button>

          <div className="flex gap-2 flex-wrap">
            {/* Payment Status Badge */}
            <span className={`inline-flex items-center px-3 py-2 rounded-xl text-xs font-bold border ${
              paymentStatus === 'Completed'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}>
              💳 Payment: {paymentStatus}
            </span>

            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Print / Download PDF
            </button>
          </div>
        </div>

        {/* ── EXACT SAME INVOICE DOCUMENT ─────────────────────────────────────── */}
        <div ref={printRef} className="bg-white border border-gray-200 rounded-3xl shadow-lg overflow-hidden print-break">
          {/* HEADER BANNER - EXACT SAME */}
          <div
            className="relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2440 60%, #162d4a 100%)' }}
          >
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.15)' }} />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.1)' }} />

            <div className="relative px-6 sm:px-8 py-6 sm:py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden bg-white/10 flex items-center justify-center border border-white/20 shrink-0 shadow-lg">
                  <img
                    src="/okmechanics.png"
                    alt="OkMechanics Logo"
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<span style="font-size:28px;font-weight:900;color:white">OK</span>';
                    }}
                  />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">OkMechanics</h1>
                  <p className="text-blue-200 text-sm mt-0.5">Professional Bike Service Network</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-white text-[11px] font-semibold">
                      🧾 TAX INVOICE
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                        paymentStatus === 'Completed' ? 'bg-green-400/20 border-green-300/40 text-green-200' : 'bg-amber-400/20 border-amber-300/40 text-amber-200'
                      }`}
                    >
                      {paymentStatus === 'Completed' ? '✅ Paid' : '⏳ Unpaid'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 px-5 py-4 text-sm space-y-2 shrink-0">
                <div className="flex justify-between gap-8">
                  <span className="text-blue-200 text-xs uppercase tracking-wide font-semibold">Invoice No</span>
                  <span className="text-white font-mono font-bold">{invoiceNo}</span>
                </div>
                <div className="flex justify-between gap-8">
                  <span className="text-blue-200 text-xs uppercase tracking-wide font-semibold">Booking ID</span>
                  <span className="text-white font-mono font-bold">#{invoice.booking_id}</span>
                </div>
                <div className="flex justify-between gap-8">
                  <span className="text-blue-200 text-xs uppercase tracking-wide font-semibold">GST</span>
                  <span className="text-white text-xs font-medium">{invoice.assignedStore?.gst_number || '—'}</span>
                </div>
                <div className="flex justify-between gap-8">
                  <span className="text-blue-200 text-xs uppercase tracking-wide font-semibold">Issue Date</span>
                  <span className="text-white text-xs font-medium">{fmtDate(issueDate)}</span>
                </div>
                <div className="flex justify-between gap-8 pt-1 border-t border-white/20">
                  <span className="text-blue-200 text-xs uppercase tracking-wide font-semibold">Status</span>
                  <span className="text-white text-xs font-bold">{bookingStatus}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── BILLED TO / SERVICE PROVIDER - EXACT SAME WITH GST BADGE ─────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-b border-gray-100">
            <div className="px-6 sm:px-8 py-5 border-b md:border-b-0 md:border-r border-gray-100">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-3">📋 Billed To</p>
              <p className="text-base font-extrabold text-gray-900">{fmt(invoice.user?.name)}</p>
              <p className="text-sm text-gray-600 mt-0.5">{fmt(invoice.user?.phone)}</p>
              <p className="text-sm text-gray-500">{fmt(invoice.user?.email)}</p>
              {isDoorstep && invoice.doorstep_flat_no && (
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  {[
                    invoice.doorstep_flat_no,
                    invoice.doorstep_area,
                    invoice.doorstep_landmark,
                    invoice.doorstep_city,
                    invoice.doorstep_pincode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>

            <div className="px-6 sm:px-8 py-5">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-3">🏪 Service Provider</p>
              {invoice.assignedStore ? (
                <>
                  <p className="text-base font-extrabold text-gray-900">{fmt(invoice.assignedStore?.name)}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{fmt(invoice.assignedStore?.contact)}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{fmt(invoice.assignedStore?.address)}</p>

                  {/* ✅ GST NUMBER BADGE */}
                  {invoice.assignedStore?.gst_number && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-100">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">GST</span>
                      <span className="text-xs font-mono font-bold text-blue-700">{invoice.assignedStore.gst_number}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">No store assigned</p>
              )}
            </div>
          </div>

          {/* ── ALL OTHER SECTIONS - EXACT SAME AS ADMIN ───────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border-b border-gray-100">
            <div className="px-6 sm:px-8 py-5 border-b sm:border-b-0 sm:border-r border-gray-100">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-3">🏍️ Vehicle Details</p>
              <InfoRow label="Brand" value={fmt(invoice.bike_brand || invoice.bikebrand)} />
              <InfoRow label="Model" value={fmt(invoice.bike_model || invoice.bikemodel)} />
              <InfoRow label="Fuel Type" value={fmt(invoice.fuel_type || invoice.fueltype)} />
              <InfoRow label="Vehicle No" value={fmt(invoice.vehicle_number || invoice.vehiclenumber)} mono />
              {invoice.kilometer && <InfoRow label="KM Driven" value={`${invoice.kilometer} km`} />}
            </div>

            <div className="px-6 sm:px-8 py-5 border-b sm:border-b-0 sm:border-r border-gray-100">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-3">🔧 Service Info</p>
              <InfoRow label="Service Type" value={fmt(invoice.service_type || invoice.servicetype)} />
              <InfoRow label="Date" value={fmt(invoice.service_date || invoice.servicedate)} />
              <InfoRow label="Time Slot" value={fmt(invoice.service_time_slot || invoice.servicetimeslot)} />
              <InfoRow label="Mode" value={isDoorstep ? '🚗 Doorstep' : '🏪 Drop-in'} />
            </div>

            <div className="px-6 sm:px-8 py-5">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-3">💳 Payment Info</p>
              <InfoRow label="Payment Type" value={fmt(invoice.payment_type || invoice.paymenttype)} />
              <InfoRow label="Payment Status" value={paymentStatus} />
              <InfoRow label="Booking Status" value={bookingStatus} />
            </div>
          </div>

          {/* ITEMS TABLE, TOTALS, TERMS - ALL EXACT SAME AS ADMIN */}
          <div className="px-4 sm:px-8 py-6">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-4">🧾 Invoice Items</p>
            <div className="overflow-x-auto rounded-2xl border border-gray-200">
              <table className="min-w-full">
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #1e3a5f, #0f2440)' }}>
                    <th className="px-4 py-3 text-left text-[10px] font-extrabold uppercase tracking-widest text-blue-200 w-12">#</th>
                    <th className="px-4 py-3 text-left text-[10px] font-extrabold uppercase tracking-widest text-blue-200">Description</th>
                    <th className="px-4 py-3 text-right text-[10px] font-extrabold uppercase tracking-widest text-blue-200 w-16">Qty</th>
                    <th className="px-4 py-3 text-right text-[10px] font-extrabold uppercase tracking-widest text-blue-200 w-28">Rate</th>
                    <th className="px-4 py-3 text-right text-[10px] font-extrabold uppercase tracking-widest text-blue-200 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {serviceRows.length > 0 ? (
                    serviceRows.map((item, i) => (
                      <tr key={item.srNo} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                        <td className="px-4 py-3.5 text-xs text-gray-400 font-medium">{item.srNo}</td>
                        <td className="px-4 py-3.5 text-sm font-semibold text-gray-900">{item.name}</td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 text-right">{item.qty}</td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 text-right">{fmtAmt(item.rate)}</td>
                        <td className="px-4 py-3.5 text-sm font-bold text-gray-900 text-right">{fmtAmt(item.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-6 text-center text-sm text-gray-400 italic">
                        No service line items found. Showing booking total below.
                      </td>
                    </tr>
                  )}

                  {doorstepCharge > 0 && (
                    <tr className="bg-blue-50/50">
                      <td className="px-4 py-3.5 text-xs text-gray-400 font-medium">{serviceRows.length + 1}</td>
                      <td className="px-4 py-3.5 text-sm font-semibold text-gray-900">🚗 Doorstep Service Charge</td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 text-right">1</td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 text-right">{fmtAmt(100)}</td>
                      <td className="px-4 py-3.5 text-sm font-bold text-gray-900 text-right">{fmtAmt(100)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end">
              <div className="w-full max-w-xs sm:max-w-sm">
                <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Subtotal</span>
                    <span className="text-sm font-semibold text-gray-800">{fmtAmt(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      GST <span className="text-xs text-gray-400 ml-1">(5%)</span>
                    </span>
                    <span className="text-sm font-semibold text-gray-800">{fmtAmt(gst)}</span>
                  </div>
                  {doorstepCharge > 0 && (
                    <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Doorstep Charge</span>
                      <span className="text-sm font-semibold text-gray-800">{fmtAmt(doorstepCharge)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center px-5 py-4" style={{ background: 'linear-gradient(135deg, #1e3a5f, #0f2440)' }}>
                    <span className="text-base font-extrabold text-white">Total Amount</span>
                    <span className="text-xl font-extrabold text-white">{fmtAmt(total)}</span>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 shadow-sm">
                    💳 {fmt(invoice.payment_type || invoice.paymenttype)} ·{' '}
                    <span className={paymentStatus === 'Completed' ? 'text-green-600' : 'text-amber-600'}>{paymentStatus}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 px-6 sm:px-8 py-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-2">📄 Terms & Conditions</p>
                <ul className="text-xs text-gray-500 space-y-1 list-disc pl-4 leading-relaxed">
                  <li>This is a system-generated tax invoice.</li>
                  <li>Amounts are based on confirmed booking service records.</li>
                  <li>GST @ 5% applicable on all services.</li>
                  <li>Contact support for disputes or corrections.</li>
                </ul>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 mb-2">📝 Notes</p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  This invoice reflects the billing view at the time of generation. Booking status and payment status are tracked
                  independently. Thank you for choosing OkMechanics for your vehicle service needs.
                </p>
              </div>
            </div>

            <div
              className="rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ background: 'linear-gradient(135deg, #1e3a5f, #0f2440)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                  <img
                    src="/okmechanics.png"
                    alt="OkMechanics"
                    className="w-full h-full object-contain p-0.5"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<span style="font-size:13px;font-weight:900;color:white">OK</span>';
                    }}
                  />
                </div>
                <div>
                  <p className="text-white font-extrabold text-sm">OkMechanics</p>
                  <p className="text-blue-200 text-xs">Professional Bike Service Network</p>
                </div>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-white font-bold text-base">Thank you for your business! 🙏</p>
                <p className="text-blue-200 text-xs mt-0.5">Invoice generated · {invoiceNo}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewInvoice;